/*
 * Loop / AIOD Price Synchronization
 * ---------------------------------
 * Purpose:
 * Keeps Loop purchase-option pricing synchronized with pricing generated
 * by the AIOD discount app.
 *
 * What this script does:
 * - Waits for the Loop widget to load asynchronously.
 * - Updates the Loop one-time purchase price with the AIOD discounted price.
 * - Shows the original base price as the one-time compare-at price.
 * - Leaves Loop's subscription price unchanged.
 * - Chooses the subscription compare-at price:
 *     1. Uses the AIOD discounted one-time price when it is higher than
 *        the Loop subscription price.
 *     2. Uses the original base price when the AIOD discounted price is
 *        equal to or lower than the Loop subscription price.
 * - Updates the subscription badge:
 *     1. "Save Extra X%" when subscription adds savings beyond the
 *        AIOD discounted one-time price.
 *     2. "Save X%" when comparing subscription against the base price.
 * - Watches only the Loop purchase-options container for relevant changes.
 * - Detects when Loop replaces its entire purchase-options container.
 * - Rechecks prices after product variant changes.
 * - Avoids repeatedly writing identical values.
 *
 * Originally created: August 4, 2026
 * Last updated: August 5, 2026
 *
 * Dependencies:
 * - AIOD discounted price: .aiod-discounted-price
 * - AIOD original price: .aiod-original-price
 * - Loop options container: .loop-widget-purchase-options-container
 *
 * This script may require revision if Loop or AIOD changes its markup,
 * class names, or pricing behavior.
 */

(function () {
  'use strict';

  const DEBUG = false;

  const SELECTORS = {
    aiodDiscountedPrice: '.aiod-discounted-price',
    aiodOriginalPrice: '.aiod-original-price',

    loopContainer: '.loop-widget-purchase-options-container',
    oneTimeContainer: '.loop-widget-purchase-option-onetime',
    displayedPrice: '.loop-widget-purchase-option-price',
    compareAtPrice:
      '.loop-widget-purchase-option-compare-at-price',
    discountBadge:
      '.loop-widget-purchase-option-discount-badge',
    sellingPlanGroup:
      '[data-loop-widget-selling-plan-group]'
  };

  const loopObserverOptions = {
    childList: true,
    subtree: true,
    characterData: true
  };

  let loopObserver = null;
  let replacementObserver = null;
  let observedLoopContainer = null;
  let observedLoopParent = null;
  let syncTimer = null;
  let isSyncing = false;

  function debug(...messages) {
    if (DEBUG) {
      console.log('[Price Sync]', ...messages);
    }
  }

  function getText(element) {
    return element?.textContent?.trim() || '';
  }

  function setText(element, value) {
    if (!element || !value) {
      return false;
    }

    if (getText(element) === value) {
      return false;
    }

    element.textContent = value;
    return true;
  }

  function showElement(element) {
    if (!element) {
      return;
    }

    element.classList.remove('loop-display-none');
  }

  function hideElement(element) {
    if (!element) {
      return;
    }

    element.classList.add('loop-display-none');
  }

  /*
   * Converts formatted price text into a number.
   *
   * Examples:
   * "$22.40"    becomes 22.4
   * "$1,249.00" becomes 1249
   */
  function parsePrice(value) {
    if (!value) {
      return null;
    }

    const numericValue = Number(
      value
        .replace(/[^0-9.,-]/g, '')
        .replace(/,/g, '')
    );

    return Number.isFinite(numericValue)
      ? numericValue
      : null;
  }

  /*
   * Formats percentages with no unnecessary trailing decimal.
   *
   * Examples:
   * 20    becomes "20"
   * 6.67  becomes "6.7"
   * 9.25  becomes "9.3"
   */
  function formatPercentage(value) {
    if (!Number.isFinite(value)) {
      return '';
    }

    const roundedValue = Math.round(value * 10) / 10;

    return Number.isInteger(roundedValue)
      ? String(roundedValue)
      : roundedValue.toFixed(1);
  }

  function getAiodDiscountedPrice() {
    const priceElement = document.querySelector(
      SELECTORS.aiodDiscountedPrice
    );

    return getText(priceElement);
  }

  function getAiodOriginalPrice() {
    const priceElement = document.querySelector(
      SELECTORS.aiodOriginalPrice
    );

    return getText(priceElement);
  }

  /*
   * Updates the Loop subscription discount badge.
   *
   * If subscription is lower than the AIOD one-time price:
   *   "Save Extra X%"
   *
   * Otherwise, if subscription is lower than the original price:
   *   "Save X%"
   */
  function updateDiscountBadge(
    sellingPlanContainer,
    basePrice,
    aiodDiscountedPrice,
    subscriptionPrice
  ) {
    const badgeElement = sellingPlanContainer.querySelector(
      SELECTORS.discountBadge
    );

    if (!badgeElement) {
      return;
    }

    const baseNumeric = parsePrice(basePrice);
    const aiodNumeric = parsePrice(aiodDiscountedPrice);
    const subscriptionNumeric = parsePrice(
      subscriptionPrice
    );

    if (subscriptionNumeric === null) {
      hideElement(badgeElement);
      return;
    }

    let badgeText = '';

    /*
     * Subscription adds savings beyond the current AIOD
     * one-time price.
     *
     * Example:
     * AIOD price: $24.00
     * Subscription: $22.40
     *
     * ($24.00 - $22.40) / $24.00 = 6.7%
     */
    if (
      aiodNumeric !== null &&
      aiodNumeric > subscriptionNumeric
    ) {
      const extraDiscountPercentage =
        (
          (aiodNumeric - subscriptionNumeric) /
          aiodNumeric
        ) * 100;

      badgeText =
        `Save Extra ${
          formatPercentage(extraDiscountPercentage)
        }%`;
    }

    /*
     * The AIOD price is equal to or lower than the subscription
     * price. Show the subscription savings against the base price.
     */
    else if (
      baseNumeric !== null &&
      baseNumeric > subscriptionNumeric
    ) {
      const totalDiscountPercentage =
        (
          (baseNumeric - subscriptionNumeric) /
          baseNumeric
        ) * 100;

      badgeText =
        `Save ${
          formatPercentage(totalDiscountPercentage)
        }%`;
    }

    if (!badgeText) {
      hideElement(badgeElement);
      return;
    }

    setText(badgeElement, badgeText);
    showElement(badgeElement);
  }

  /*
   * Updates the Loop one-time purchase option.
   */
  function syncOneTimePrice(
    aiodDiscountedPrice,
    aiodOriginalPrice
  ) {
    const oneTimeContainer = document.querySelector(
      SELECTORS.oneTimeContainer
    );

    if (!oneTimeContainer) {
      debug('One-time purchase container not available');
      return false;
    }

    const priceElement = oneTimeContainer.querySelector(
      SELECTORS.displayedPrice
    );

    const compareAtElement =
      oneTimeContainer.querySelector(
        SELECTORS.compareAtPrice
      );

    if (!priceElement) {
      debug('One-time displayed price not available');
      return false;
    }

    /*
     * Preserve Loop's original one-time price as a fallback.
     */
    if (!oneTimeContainer.dataset.originalLoopPrice) {
      const currentLoopPrice = getText(priceElement);

      if (
        currentLoopPrice &&
        currentLoopPrice !== aiodDiscountedPrice
      ) {
        oneTimeContainer.dataset.originalLoopPrice =
          currentLoopPrice;
      }
    }

    const originalPrice =
      aiodOriginalPrice ||
      oneTimeContainer.dataset.originalLoopPrice;

    let changed = false;

    if (compareAtElement && originalPrice) {
      changed =
        setText(compareAtElement, originalPrice) ||
        changed;

      showElement(compareAtElement);
    }

    changed =
      setText(priceElement, aiodDiscountedPrice) ||
      changed;

    debug('One-time price checked', {
      originalPrice,
      discountedPrice: aiodDiscountedPrice,
      changed
    });

    return true;
  }

  /*
   * Updates subscription compare-at prices and badges.
   *
   * Loop's actual subscription price remains unchanged.
   */
  function syncSubscriptionPrices(
    aiodDiscountedPrice,
    aiodOriginalPrice
  ) {
    const aiodDiscountedNumeric = parsePrice(
      aiodDiscountedPrice
    );

    const aiodOriginalNumeric = parsePrice(
      aiodOriginalPrice
    );

    if (aiodDiscountedNumeric === null) {
      debug(
        'AIOD discounted price could not be parsed',
        aiodDiscountedPrice
      );

      return false;
    }

    const sellingPlanContainers =
      document.querySelectorAll(
        SELECTORS.sellingPlanGroup
      );

    if (!sellingPlanContainers.length) {
      debug(
        'Subscription purchase containers not available'
      );

      return false;
    }

    sellingPlanContainers.forEach(
      (sellingPlanContainer) => {
        const subscriptionPriceElement =
          sellingPlanContainer.querySelector(
            SELECTORS.displayedPrice
          );

        const compareAtElement =
          sellingPlanContainer.querySelector(
            SELECTORS.compareAtPrice
          );

        const badgeElement =
          sellingPlanContainer.querySelector(
            SELECTORS.discountBadge
          );

        if (!subscriptionPriceElement) {
          return;
        }

        /*
         * Save Loop's original subscription price before
         * making any display changes.
         */
        if (
          !sellingPlanContainer.dataset
            .originalSubscriptionPrice
        ) {
          const currentSubscriptionPrice =
            getText(subscriptionPriceElement);

          if (currentSubscriptionPrice) {
            sellingPlanContainer.dataset
              .originalSubscriptionPrice =
              currentSubscriptionPrice;
          }
        }

        const subscriptionPrice =
          sellingPlanContainer.dataset
            .originalSubscriptionPrice;

        const subscriptionNumericPrice =
          parsePrice(subscriptionPrice);

        if (
          !subscriptionPrice ||
          subscriptionNumericPrice === null
        ) {
          debug(
            'Loop subscription price could not be parsed',
            subscriptionPrice
          );

          return;
        }

        /*
         * Keep Loop's original subscription price displayed.
         */
        setText(
          subscriptionPriceElement,
          subscriptionPrice
        );

        /*
         * Select the subscription compare-at price.
         *
         * If the AIOD one-time price is higher than the
         * subscription price, compare subscription against
         * the AIOD price.
         *
         * If the AIOD price is equal to or lower than the
         * subscription price, compare subscription against
         * the original base price.
         */
        let subscriptionCompareAtPrice = '';

        if (
          aiodDiscountedNumeric >
          subscriptionNumericPrice
        ) {
          subscriptionCompareAtPrice =
            aiodDiscountedPrice;
        } else if (
          aiodOriginalPrice &&
          aiodOriginalNumeric !== null &&
          aiodOriginalNumeric >
            subscriptionNumericPrice
        ) {
          subscriptionCompareAtPrice =
            aiodOriginalPrice;
        }

        if (
          compareAtElement &&
          subscriptionCompareAtPrice
        ) {
          setText(
            compareAtElement,
            subscriptionCompareAtPrice
          );

          showElement(compareAtElement);

          updateDiscountBadge(
            sellingPlanContainer,
            aiodOriginalPrice,
            aiodDiscountedPrice,
            subscriptionPrice
          );
        } else {
          hideElement(compareAtElement);
          hideElement(badgeElement);
        }

        debug('Subscription price checked', {
          originalPrice: aiodOriginalPrice,
          discountedOneTimePrice:
            aiodDiscountedPrice,
          subscriptionPrice,
          subscriptionCompareAtPrice,
          badge: getText(badgeElement)
        });
      }
    );

    return true;
  }

  function reconnectLoopObserver() {
    if (
      loopObserver &&
      observedLoopContainer &&
      observedLoopContainer.isConnected
    ) {
      loopObserver.observe(
        observedLoopContainer,
        loopObserverOptions
      );
    }
  }

  function syncLoopPrices() {
    if (isSyncing) {
      return;
    }

    isSyncing = true;

    /*
     * Temporarily disconnect the observer so this script
     * does not react to its own changes.
     */
    loopObserver?.disconnect();

    try {
      const aiodDiscountedPrice =
        getAiodDiscountedPrice();

      const aiodOriginalPrice =
        getAiodOriginalPrice();

      if (!aiodDiscountedPrice) {
        debug('AIOD discounted price not available');
        return;
      }

      syncOneTimePrice(
        aiodDiscountedPrice,
        aiodOriginalPrice
      );

      syncSubscriptionPrices(
        aiodDiscountedPrice,
        aiodOriginalPrice
      );
    } catch (error) {
      console.error(
        '[Price Sync] Synchronization error:',
        error
      );
    } finally {
      reconnectLoopObserver();
      isSyncing = false;
    }
  }

  function scheduleSync(delay = 150) {
    clearTimeout(syncTimer);

    syncTimer = setTimeout(() => {
      syncLoopPrices();
    }, delay);
  }

  function attachLoopObserver(loopContainer) {
    if (!loopContainer) {
      return;
    }

    /*
     * Avoid attaching another observer to the same container.
     */
    if (
      observedLoopContainer === loopContainer &&
      loopObserver
    ) {
      return;
    }

    loopObserver?.disconnect();

    observedLoopContainer = loopContainer;

    loopObserver = new MutationObserver(() => {
      scheduleSync();
    });

    loopObserver.observe(
      observedLoopContainer,
      loopObserverOptions
    );

    debug('Attached observer to Loop container');

    attachReplacementObserver(loopContainer);
    scheduleSync(0);
  }

  function attachReplacementObserver(loopContainer) {
    const parent = loopContainer.parentElement;

    if (!parent) {
      return;
    }

    if (
      observedLoopParent === parent &&
      replacementObserver
    ) {
      return;
    }

    replacementObserver?.disconnect();

    observedLoopParent = parent;

    /*
     * Detect when Loop replaces the complete options container.
     */
    replacementObserver = new MutationObserver(() => {
      const currentLoopContainer =
        document.querySelector(
          SELECTORS.loopContainer
        );

      if (
        currentLoopContainer &&
        currentLoopContainer !==
          observedLoopContainer
      ) {
        debug('Loop container was replaced');

        attachLoopObserver(
          currentLoopContainer
        );
      }
    });

    replacementObserver.observe(
      observedLoopParent,
      {
        childList: true
      }
    );
  }

  function waitForLoopContainer() {
    const existingContainer = document.querySelector(
      SELECTORS.loopContainer
    );

    if (existingContainer) {
      attachLoopObserver(existingContainer);
      return;
    }

    /*
     * This temporary observer exists only until Loop
     * initially inserts its options container.
     */
    const initialLoadObserver =
      new MutationObserver(() => {
        const loopContainer =
          document.querySelector(
            SELECTORS.loopContainer
          );

        if (!loopContainer) {
          return;
        }

        initialLoadObserver.disconnect();

        attachLoopObserver(loopContainer);
      });

    initialLoadObserver.observe(document.body, {
      childList: true,
      subtree: true
    });

    /*
     * Stop the temporary full-page observer if Loop
     * does not load within 15 seconds.
     */
    setTimeout(() => {
      initialLoadObserver.disconnect();
    }, 15000);
  }

  function resetStoredPrices() {
    const oneTimeContainer = document.querySelector(
      SELECTORS.oneTimeContainer
    );

    if (oneTimeContainer) {
      delete oneTimeContainer.dataset
        .originalLoopPrice;
    }

    document
      .querySelectorAll(
        SELECTORS.sellingPlanGroup
      )
      .forEach((sellingPlanContainer) => {
        delete sellingPlanContainer.dataset
          .originalSubscriptionPrice;
      });
  }

  function handleProductChange(event) {
    const target = event.target;

    if (!(target instanceof Element)) {
      return;
    }

    const isVariantControl = target.matches(
      'input[name="id"], ' +
      'select[name="id"], ' +
      '[name^="options["], ' +
      '[data-option-value]'
    );

    if (!isVariantControl) {
      return;
    }

    /*
     * Allow Loop and AIOD time to update after
     * the product variant changes.
     */
    resetStoredPrices();

    scheduleSync(100);
    setTimeout(syncLoopPrices, 500);
    setTimeout(syncLoopPrices, 1500);
  }

  function setupPriceSync() {
    waitForLoopContainer();

    document.addEventListener(
      'change',
      handleProductChange
    );

    /*
     * Backup checks while Loop and AIOD initially load.
     */
    setTimeout(syncLoopPrices, 500);
    setTimeout(syncLoopPrices, 1000);
    setTimeout(syncLoopPrices, 2000);
    setTimeout(syncLoopPrices, 3000);
    setTimeout(syncLoopPrices, 5000);
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      setupPriceSync,
      { once: true }
    );
  } else {
    setupPriceSync();
  }
})();